<template>
  <div class="schedule-workspace" :class="{ fullscreen }">
    <header class="schedule-toolbar">
      <button type="button" class="icon-button" title="Voltar ao quadro" aria-label="Voltar ao quadro" @click="exitToBoard">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <div class="schedule-heading">
        <h2><small>Cronograma</small>{{ project.name }}</h2>
        <p>
          {{ demands.length }} demandas · {{ scheduledCount }} planejadas
          <template v-if="unscheduled.length"> · <button type="button" class="stat-link" :class="{ active: unscheduledOpen }" @click="toggleUnscheduled">{{ unscheduled.length }} sem história</button></template>
          <template v-if="lateCount"> · <button type="button" class="stat-link late" :class="{ active: onlyLate }" :title="onlyLate ? 'Mostrar todas' : 'Mostrar só as atrasadas'" @click="onlyLate = !onlyLate">{{ lateCount }} {{ lateCount === 1 ? 'atrasada' : 'atrasadas' }}</button></template>
        </p>
      </div>
      <input v-model="search" type="search" class="schedule-search" placeholder="Buscar tarefa, história ou responsável" aria-label="Buscar no cronograma">
      <button type="button" class="view-toggle" :class="{ active: hideCompleted }" @click="hideCompleted = !hideCompleted">Ocultar concluídas</button>
      <button type="button" class="icon-button" :title="allCollapsed ? 'Expandir tudo' : 'Recolher tudo'" :aria-label="allCollapsed ? 'Expandir tudo' : 'Recolher tudo'" @click="setAllCollapsed(!allCollapsed)">
        <svg v-if="allCollapsed" width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 2.5h10M3 13.5h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        <svg v-else width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 10l4-4 4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 2.5h10M3 13.5h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
      </button>
      <span class="toolbar-spacer"></span>
      <div class="schedule-zoom" aria-label="Escala do cronograma">
        <button v-for="mode in zoomModes" :key="mode.value" type="button" :class="{ active: zoom === mode.value }" @click="setZoom(mode.value)">{{ mode.label }}</button>
      </div>
      <button type="button" class="view-toggle" @click="scrollToToday">Hoje</button>
      <div class="legend-wrap">
        <button type="button" class="icon-button" aria-label="Legenda">?</button>
        <div class="schedule-legend" role="tooltip">
          <span><i class="legend-swatch priority-1"></i>Prioridade alta</span>
          <span><i class="legend-swatch priority-2"></i>Prioridade média</span>
          <span><i class="legend-swatch priority-3"></i>Prioridade baixa</span>
          <span><i class="legend-swatch done"></i>Concluída</span>
          <span><i class="legend-swatch late"></i>Atrasada</span>
          <span><i class="legend-due"></i>Prazo de entrega</span>
          <span><i class="legend-today"></i>Hoje</span>
        </div>
      </div>
      <button type="button" class="icon-button" :class="{ active: fullscreen }" :title="fullscreen ? 'Sair da tela cheia (Esc)' : 'Tela cheia'" :aria-label="fullscreen ? 'Sair da tela cheia' : 'Tela cheia'" @click="toggleFullscreen">
        <svg v-if="!fullscreen" width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <svg v-else width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M6 2v4H2M14 6h-4V2M10 14v-4h4M2 10h4v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
      <span class="toolbar-divider"></span>
      <button v-if="isStaff" type="button" class="schedule-button is-primary" @click="moduleComposerOpen = true">+ Módulo</button>
      <button type="button" class="icon-button" title="Exportar para Excel" aria-label="Exportar para Excel" @click="$emit('export')">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M4.5 6.5L8 10l3.5-3.5M3 13.5h10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
      </button>
    </header>

    <div v-if="error" class="schedule-alert">
      <span>{{ error }}</span>
      <button type="button" aria-label="Fechar aviso" @click="error = ''">×</button>
    </div>

    <form v-if="moduleComposerOpen" class="schedule-composer module-composer" @submit.prevent="createModule">
      <input ref="moduleInput" v-model="moduleDraft" maxlength="180" placeholder="Nome do módulo" aria-label="Nome do módulo">
      <button type="submit" :disabled="saving || !moduleDraft.trim()">Adicionar</button>
      <button type="button" @click="closeModuleComposer">Cancelar</button>
    </form>

    <div class="schedule-body">
    <aside v-if="unscheduledOpen && unscheduled.length" class="unscheduled-drawer" :style="{ height: `${sheetMaxHeight}px` }">
      <header class="unscheduled-heading">
        <div><strong>Sem história <b>{{ unscheduled.length }}</b></strong><small>Classifique as demandas do Kanban em uma história</small></div>
        <button type="button" class="icon-button" aria-label="Fechar" @click="unscheduledOpen = false">×</button>
      </header>
      <label class="unscheduled-tools"><input v-model="showCompletedUnscheduled" type="checkbox"> Mostrar concluídas ({{ unscheduledCompletedCount }})</label>
      <div class="unscheduled-list">
        <div v-for="demand in unscheduledList" :key="demand.id" class="unscheduled-item">
          <button type="button" class="unscheduled-title" :title="demand.title" @click="$emit('open-task', demand)">
            <small>{{ demand.displayId }} · {{ demand.status }}</small><span>{{ demand.title }}</span>
          </button>
          <select v-model="planningDrafts[demand.id].storyId" :disabled="!isStaff" aria-label="História">
            <option value="">Selecionar história</option>
            <optgroup v-for="module in modules" :key="module.id" :label="module.name">
              <option v-for="story in storiesForModule(module.id)" :key="story.id" :value="story.id">{{ story.title }}</option>
            </optgroup>
          </select>
          <input v-model="planningDrafts[demand.id].plannedStartDate" type="date" :disabled="!isStaff" aria-label="Início planejado">
          <input v-model="planningDrafts[demand.id].plannedEndDate" type="date" :disabled="!isStaff" aria-label="Fim planejado">
          <button v-if="isStaff" type="button" :disabled="saving || !planningDrafts[demand.id].storyId" @click="planDemand(demand)">Planejar</button>
        </div>
        <div v-if="!unscheduledList.length" class="unscheduled-empty">Nenhuma demanda aberta sem história.</div>
      </div>
    </aside>

    <div v-if="loading" class="schedule-empty">Carregando cronograma...</div>
    <div v-else-if="!modules.length" class="schedule-empty">
      <strong>Comece organizando o projeto em módulos</strong>
      <span>Depois, crie histórias e adicione as tarefas que também aparecerão no Backlog.</span>
      <button v-if="isStaff" type="button" @click="moduleComposerOpen = true">Criar primeiro módulo</button>
    </div>
    <div v-else ref="sheetScroll" class="schedule-sheet-scroll" :style="{ maxHeight: `${sheetMaxHeight}px` }">
      <div class="schedule-sheet" :class="{ compact: compactColumns, wide: wideColumns }" :style="sheetStyle">
        <div class="schedule-grid-header schedule-left-cell">
          <span>Item</span>
          <template v-if="!compactColumns">
            <span>Status</span>
            <template v-if="wideColumns"><span>Responsável</span><span>Prior.</span></template>
            <span>Início</span><span>Fim</span>
          </template>
          <span class="align-center">%</span>
        </div>
        <div class="schedule-timeline-header schedule-right-cell">
          <div class="timeline-months">
            <span v-for="month in timelineMonths" :key="month.key" :style="{ width: `${month.width}px` }">{{ month.label }}</span>
          </div>
          <div class="timeline-days">
            <span v-for="day in timelineDays" :key="day.key" class="timeline-day" :class="{ weekend: day.weekend, today: day.today, marked: day.showLabel }" :style="{ width: dayWidth + 'px' }">
              <b v-if="day.showLabel">{{ day.label }}</b>
            </span>
          </div>
          <i v-if="todayOffset >= 0" class="today-line header" :style="{ left: `${todayOffset}px` }"></i>
        </div>

        <template v-for="module in visibleModules">
          <div :key="`module-left-${module.id}`" class="schedule-left-cell group-row module-row">
            <button type="button" class="collapse-button" :aria-label="isModuleCollapsed(module.id) ? 'Expandir módulo' : 'Recolher módulo'" @click="toggleModule(module.id)">{{ isModuleCollapsed(module.id) ? '▸' : '▾' }}</button>
            <div class="group-title">
              <input v-if="editingModuleId === module.id" v-model="editingLabel" v-focus maxlength="180" @keydown.enter.prevent="saveModule(module)" @keydown.esc.stop.prevent="cancelEditing" @blur="saveModule(module)">
              <template v-else><strong :title="module.name" @dblclick="isStaff && beginModuleEditing(module)">{{ module.name }}</strong></template>
            </div>
            <template v-if="!compactColumns">
              <span class="group-metric" title="Concluídas / total">{{ moduleSummary(module.id).done }}/{{ moduleSummary(module.id).total }}</span>
              <span class="group-date">{{ formatShortDate(moduleSummary(module.id).start) }}</span>
              <span class="group-date">{{ formatShortDate(moduleSummary(module.id).end) }}</span>
            </template>
            <span class="group-progress">{{ moduleSummary(module.id).progress }}%</span>
            <div v-if="isStaff && editingModuleId !== module.id" class="row-actions">
              <button type="button" @click="openStoryComposer(module.id)">+ História</button>
              <button type="button" @click="beginModuleEditing(module)">Renomear</button>
              <button type="button" class="danger" @click="removeModule(module)">Excluir</button>
            </div>
          </div>
          <div :key="`module-right-${module.id}`" class="schedule-right-cell timeline-cell group-timeline" :style="timelineBackgroundStyle">
            <div v-if="moduleSummary(module.id).start" class="summary-bar module-summary" :style="barStyle(moduleSummary(module.id))">
              <i :style="{ width: `${moduleSummary(module.id).progress}%` }"></i>
            </div>
            <i v-if="todayOffset >= 0" class="today-line" :style="{ left: `${todayOffset}px` }"></i>
          </div>

          <template v-if="!isModuleCollapsed(module.id)">
            <template v-for="story in visibleStoriesForModule(module.id)">
              <div :key="`story-left-${story.id}`" class="schedule-left-cell group-row story-row">
                <button type="button" class="collapse-button" :aria-label="isStoryCollapsed(story.id) ? 'Expandir história' : 'Recolher história'" @click="toggleStory(story.id)">{{ isStoryCollapsed(story.id) ? '▸' : '▾' }}</button>
                <div class="group-title">
                  <input v-if="editingStoryId === story.id" v-model="editingLabel" v-focus maxlength="220" @keydown.enter.prevent="saveStory(story)" @keydown.esc.stop.prevent="cancelEditing" @blur="saveStory(story)">
                  <template v-else><strong :title="story.title" @dblclick="isStaff && beginStoryEditing(story)">{{ story.title }}</strong></template>
                </div>
                <template v-if="!compactColumns">
                  <span class="group-metric" title="Concluídas / total">{{ storySummary(story.id).done }}/{{ storySummary(story.id).total }}</span>
                  <span class="group-date">{{ formatShortDate(storySummary(story.id).start) }}</span>
                  <span class="group-date">{{ formatShortDate(storySummary(story.id).end) }}</span>
                </template>
                <span class="group-progress">{{ storySummary(story.id).progress }}%</span>
                <div v-if="isStaff && editingStoryId !== story.id" class="row-actions">
                  <button type="button" @click="openTaskComposer(story.id)">+ Tarefa</button>
                  <button type="button" @click="beginStoryEditing(story)">Renomear</button>
                  <button type="button" class="danger" @click="removeStory(story)">Excluir</button>
                </div>
              </div>
              <div :key="`story-right-${story.id}`" class="schedule-right-cell timeline-cell story-timeline" :style="timelineBackgroundStyle">
                <div v-if="storySummary(story.id).start" class="summary-bar story-summary" :style="barStyle(storySummary(story.id))">
                  <i :style="{ width: `${storySummary(story.id).progress}%` }"></i>
                </div>
                <i v-if="todayOffset >= 0" class="today-line" :style="{ left: `${todayOffset}px` }"></i>
              </div>

              <template v-if="!isStoryCollapsed(story.id)">
                <template v-for="demand in visibleDemandsForStory(story.id)">
                  <div :key="`task-left-${demand.id}`" class="schedule-left-cell task-row" :class="{ 'is-late': isLate(demand), 'is-done': isCompleted(demand) }">
                    <div class="task-title-cell">
                      <button type="button" class="task-id" title="Abrir detalhes" @click="$emit('open-task', demand)">{{ demand.displayId }}</button>
                      <input
                        v-if="isStaff"
                        class="cell-input title-input"
                        data-cell="title"
                        :value="demand.title"
                        :title="demand.title"
                        maxlength="220"
                        aria-label="Título"
                        @blur="updateDemandField(demand, 'title', $event)"
                        @keydown.enter.prevent="moveCell($event, 1)"
                        @keydown.down.prevent="moveCell($event, 1)"
                        @keydown.up.prevent="moveCell($event, -1)"
                        @keydown.esc.stop.prevent="revertCell($event, demand.title)"
                      >
                      <button v-else type="button" class="task-title" :title="demand.title" @click="$emit('open-task', demand)">{{ demand.title }}</button>
                      <button type="button" class="open-task" title="Abrir detalhes" aria-label="Abrir detalhes" @click="$emit('open-task', demand)">
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M6 3H3v10h10v-3M9 3h4v4M13 3L7 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
                      </button>
                    </div>
                    <template v-if="!compactColumns">
                      <label class="status-cell" :title="demand.status">
                        <i :class="statusClass(demand.status)"></i>
                        <select v-if="isStaff" class="cell-select" :value="demand.status" aria-label="Status" @change="updateDemandField(demand, 'status', $event)">
                          <option v-for="status in statusOptionsFor(demand)" :key="status" :value="status">{{ status }}</option>
                        </select>
                        <span v-else>{{ demand.status }}</span>
                      </label>
                      <template v-if="wideColumns">
                        <select v-if="isStaff" class="cell-select" :class="{ muted: !demand.ownerUserId }" :value="demand.ownerUserId || ''" :title="demand.owner || 'Sem responsável'" aria-label="Responsável" @change="updateDemandField(demand, 'ownerUserId', $event)">
                          <option value="">Sem responsável</option>
                          <option v-for="owner in ownerOptionsFor(demand)" :key="owner.value" :value="owner.value">{{ owner.text }}</option>
                        </select>
                        <span v-else class="cell-text" :title="demand.owner">{{ demand.owner || '—' }}</span>
                        <select v-if="isStaff" class="cell-select priority-select" :class="`priority-${demand.priority}`" :value="demand.priority" aria-label="Prioridade" @change="updateDemandField(demand, 'priority', $event)">
                          <option v-for="option in priorityOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                        </select>
                        <span v-else class="cell-text priority-select" :class="`priority-${demand.priority}`">{{ priorityLabel(demand.priority) }}</span>
                      </template>
                      <input :value="demand.plannedStartDate || ''" type="date" :disabled="!isStaff" aria-label="Início planejado" @change="updateDemandField(demand, 'plannedStartDate', $event)">
                      <input :value="demand.plannedEndDate || ''" type="date" :class="{ late: isLate(demand) }" :disabled="!isStaff" aria-label="Fim planejado" @change="updateDemandField(demand, 'plannedEndDate', $event)">
                      <input :value="demand.progress || 0" type="number" min="0" max="100" step="5" :disabled="!isStaff || isCompleted(demand)" aria-label="Progresso" @change="updateDemandField(demand, 'progress', $event)">
                    </template>
                    <span v-else class="group-progress">{{ Number(demand.progress || 0) }}%</span>
                  </div>
                  <div :key="`task-right-${demand.id}`" class="schedule-right-cell timeline-cell task-timeline" :style="timelineBackgroundStyle">
                    <div
                      v-if="demand.plannedStartDate && demand.plannedEndDate"
                      class="timeline-bar task-bar"
                      :class="[`priority-${demand.priority}`, { completed: isCompleted(demand), late: isLate(demand) }]"
                      :style="barStyle(demand)"
                      :title="barTooltip(demand)"
                      @click="$emit('open-task', demand)"
                    >
                      <i class="bar-progress" :style="{ width: `${Number(demand.progress || 0)}%` }"></i>
                      <span class="bar-label">{{ demand.title }}<em v-if="demand.owner"> · {{ demand.owner }}</em></span>
                    </div>
                    <span v-else class="missing-date">Defina início e fim</span>
                    <i
                      v-if="dueOffset(demand) >= 0"
                      class="due-marker"
                      :class="{ overdue: isPastDue(demand) }"
                      :style="{ left: `${dueOffset(demand)}px` }"
                      :title="`Prazo de entrega: ${formatShortDate(demand.dueDate)}`"
                    ></i>
                    <i v-if="todayOffset >= 0" class="today-line" :style="{ left: `${todayOffset}px` }"></i>
                  </div>
                </template>

                <form v-if="isStaff && taskComposerStoryId === story.id" :key="`task-form-left-${story.id}`" class="schedule-left-cell task-composer-row" @submit.prevent="createTask(story)">
                  <input v-model="taskDraft.title" v-focus class="task-name-input" maxlength="220" placeholder="Nova tarefa (Enter para criar)">
                  <input v-model="taskDraft.plannedStartDate" type="date" aria-label="Início">
                  <input v-model="taskDraft.plannedEndDate" type="date" aria-label="Fim">
                  <button type="submit" :disabled="saving || !taskDraft.title.trim()">Criar</button>
                  <button type="button" class="ghost" @click="closeTaskComposer">×</button>
                </form>
                <div v-if="isStaff && taskComposerStoryId === story.id" :key="`task-form-right-${story.id}`" class="schedule-right-cell composer-timeline"></div>

                <template v-if="isStaff && taskComposerStoryId !== story.id && !demandsForStory(story.id).length">
                  <div :key="`add-task-left-${story.id}`" class="schedule-left-cell add-row"><button type="button" @click="openTaskComposer(story.id)">+ Adicionar primeira tarefa</button></div>
                  <div :key="`add-task-right-${story.id}`" class="schedule-right-cell add-row-timeline"></div>
                </template>
              </template>
            </template>

            <form v-if="isStaff && storyComposerModuleId === module.id" :key="`story-form-left-${module.id}`" class="schedule-left-cell story-composer" @submit.prevent="createStory(module)">
              <input v-model="storyDraft" v-focus maxlength="220" placeholder="Nome da história (Enter para criar)">
              <button type="submit" :disabled="saving || !storyDraft.trim()">Adicionar</button>
              <button type="button" class="ghost" @click="closeStoryComposer">×</button>
            </form>
            <div v-if="isStaff && storyComposerModuleId === module.id" :key="`story-form-right-${module.id}`" class="schedule-right-cell composer-timeline"></div>
            <template v-if="isStaff && storyComposerModuleId !== module.id && !storiesForModule(module.id).length">
              <div :key="`add-story-left-${module.id}`" class="schedule-left-cell add-row story-level"><button type="button" @click="openStoryComposer(module.id)">+ Adicionar primeira história</button></div>
              <div :key="`add-story-right-${module.id}`" class="schedule-right-cell add-row-timeline"></div>
            </template>
          </template>
        </template>

        <div v-if="!visibleModules.length" class="schedule-left-cell schedule-no-results">Nenhum item corresponde aos filtros.</div>
        <div v-if="!visibleModules.length" class="schedule-right-cell add-row-timeline"></div>
      </div>
    </div>
    <span
      v-if="!loading && modules.length"
      class="column-resizer"
      :class="{ active: resizing }"
      :style="{ left: `${leftWidth - 3}px`, height: `${sheetMaxHeight}px` }"
      title="Arraste para ajustar a largura da tabela · duplo clique para alternar"
      @mousedown.prevent="startResize"
      @dblclick="toggleColumns"
    ></span>
    </div>
  </div>
</template>

<script>
import api from '../services/api'

const DAY = 86400000

// View preferences are per-browser conveniences; storage can be unavailable (private mode).
function readStoredString (key) { try { return window.localStorage.getItem(key) || '' } catch (error) { return '' } }
function readStoredNumber (key) { return Number(readStoredString(key)) || 0 }
function writeStored (key, value) { try { window.localStorage.setItem(key, String(value)) } catch (error) { /* ignore */ } }

export default {
  name: 'ScheduleWorkspace',
  directives: {
    focus: { inserted (el) { el.focus() } }
  },
  props: {
    project: { type: Object, required: true },
    demands: { type: Array, required: true },
    isStaff: { type: Boolean, default: false },
    statusOptions: { type: Array, default: () => [] },
    ownerOptions: { type: Array, default: () => [] }
  },
  data () {
    return {
      modules: [], stories: [], loading: false, saving: false, error: '', zoom: ['day', 'week', 'month'].includes(readStoredString('demandesk.schedule.zoom')) ? readStoredString('demandesk.schedule.zoom') : 'week',
      zoomModes: [{ label: 'Dia', value: 'day' }, { label: 'Semana', value: 'week' }, { label: 'Mês', value: 'month' }],
      moduleComposerOpen: false, moduleDraft: '', storyComposerModuleId: '', storyDraft: '',
      taskComposerStoryId: '', taskDraft: { title: '', plannedStartDate: '', plannedEndDate: '' },
      collapsedModules: {}, collapsedStories: {}, unscheduledOpen: false, showCompletedUnscheduled: false,
      planningDrafts: {}, planningDraftBases: {},
      editingModuleId: '', editingStoryId: '', editingLabel: '',
      search: '', hideCompleted: false, onlyLate: false, fullscreen: false, resizing: false,
      leftWidth: readStoredNumber('demandesk.schedule.leftWidth') || 600,
      columnsSizedByUser: Boolean(readStoredNumber('demandesk.schedule.leftWidth')),
      priorityOptions: [{ text: 'Alta', value: 1 }, { text: 'Média', value: 2 }, { text: 'Baixa', value: 3 }],
      sheetMaxHeight: 560, scrolledToToday: false, today: this.dateOnly(new Date())
    }
  },
  computed: {
    dayWidth () { return this.zoom === 'day' ? 34 : this.zoom === 'week' ? 18 : 7 },
    compactColumns () { return this.leftWidth < 470 },
    wideColumns () { return this.leftWidth >= 760 },
    scheduledCount () { return this.demands.filter(item => item.plannedStartDate && item.plannedEndDate).length },
    unscheduled () { return this.demands.filter(item => !item.storyId) },
    unscheduledCompletedCount () { return this.unscheduled.filter(this.isCompleted).length },
    unscheduledList () { return this.showCompletedUnscheduled ? this.unscheduled : this.unscheduled.filter(item => !this.isCompleted(item)) },
    lateCount () { return this.demands.filter(item => item.storyId && this.isLate(item)).length },
    normalizedSearch () { return this.normalize(this.search) },
    filtersActive () { return Boolean(this.normalizedSearch || this.hideCompleted || this.onlyLate) },
    visibleModules () { return this.modules.filter(module => !this.filtersActive || this.visibleStoriesForModule(module.id).length || (!this.onlyLate && !this.hideCompleted && this.matches(module.name))) },
    allCollapsed () { return this.modules.length > 0 && this.modules.every(module => this.collapsedModules[module.id]) },
    storyDemands () { return this.demands.filter(item => item.storyId && (item.plannedStartDate || item.plannedEndDate)) },
    timelineBounds () {
      const today = this.today
      const dates = this.storyDemands.flatMap(item => [item.plannedStartDate, item.plannedEndDate]).filter(Boolean).sort()
      const first = dates[0] || today
      const last = dates[dates.length - 1] || today
      return { start: this.addDays(first < today ? first : today, -7), end: this.addDays(last > today ? last : today, 21) }
    },
    timelineDays () {
      const days = []
      const total = Math.min(730, this.daysBetween(this.timelineBounds.start, this.timelineBounds.end) + 1)
      for (let index = 0; index < total; index++) {
        const key = this.addDays(this.timelineBounds.start, index)
        const date = new Date(`${key}T12:00:00`)
        const day = date.getDay()
        const showLabel = this.zoom === 'day' || (this.zoom === 'week' && day === 1) || (this.zoom === 'month' && day === 1)
        const label = this.zoom === 'day' ? String(date.getDate()) : date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
        days.push({ key, weekend: day === 0 || day === 6, today: key === this.today, showLabel, label: this.zoom === 'month' ? String(date.getDate()) : label })
      }
      return days
    },
    timelineMonths () {
      const months = []
      this.timelineDays.forEach(day => {
        const key = day.key.slice(0, 7)
        const last = months[months.length - 1]
        if (last && last.key === key) last.width += this.dayWidth
        else {
          const date = new Date(`${day.key}T12:00:00`)
          const label = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
          months.push({ key, width: this.dayWidth, label: label.charAt(0).toUpperCase() + label.slice(1) })
        }
      })
      return months
    },
    timelineWidth () { return Math.max(720, this.timelineDays.length * this.dayWidth) },
    sheetStyle () { return { '--timeline-width': `${this.timelineWidth}px`, '--left-width': `${this.leftWidth}px` } },
    timelineBackgroundStyle () {
      const dw = this.dayWidth
      const gridSize = this.zoom === 'month' ? dw * 7 : dw
      const firstWeekday = new Date(`${this.timelineBounds.start}T12:00:00`).getDay()
      const saturdayOffset = ((6 - firstWeekday + 7) % 7) * dw
      const gridOffset = this.zoom === 'month' ? ((1 - firstWeekday + 7) % 7) * dw : 0
      return {
        width: `${this.timelineWidth}px`,
        backgroundImage: `linear-gradient(to right, #242427 1px, transparent 1px), linear-gradient(to right, rgba(255,255,255,.028) ${dw * 2}px, transparent ${dw * 2}px)`,
        backgroundSize: `${gridSize}px 100%, ${dw * 7}px 100%`,
        backgroundPosition: `${gridOffset}px 0, ${saturdayOffset}px 0`
      }
    },
    todayOffset () {
      const offset = this.daysBetween(this.timelineBounds.start, this.today)
      return offset >= 0 && offset < this.timelineDays.length ? offset * this.dayWidth + this.dayWidth / 2 : -1
    }
  },
  watch: {
    'project.id': { immediate: true, handler () { this.modules = []; this.stories = []; this.scrolledToToday = false; this.loadSchedule() } },
    demands: { immediate: true, deep: true, handler () { this.initializePlanningDrafts() } },
    moduleComposerOpen (open) {
      if (open) this.$nextTick(() => this.$refs.moduleInput && this.$refs.moduleInput.focus())
      this.updateSheetHeight()
    },
    unscheduledOpen () { this.updateSheetHeight() },
    error () { this.updateSheetHeight() },
    zoom (value) { writeStored('demandesk.schedule.zoom', value) },
    fullscreen (value) {
      this.$emit('fullscreen-change', value)
      this.updateSheetHeight()
    }
  },
  mounted () {
    window.addEventListener('resize', this.updateSheetHeight)
    window.addEventListener('keydown', this.onKeydown)
    this.updateSheetHeight()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.updateSheetHeight)
    window.removeEventListener('keydown', this.onKeydown)
    this.stopResize()
    if (this.fullscreen) this.$emit('fullscreen-change', false)
  },
  methods: {
    async loadSchedule () {
      if (!this.project.id) return
      // Reloads after edits keep the sheet mounted so scroll position is preserved.
      const initial = !this.modules.length
      if (initial) this.loading = true
      this.error = ''
      try {
        const result = await api.getProjectSchedule(this.project.id)
        this.modules = result.modules || []; this.stories = result.stories || []
      } catch (error) { this.error = this.errorMessage(error) } finally {
        this.loading = false
        this.updateSheetHeight()
        if (!this.scrolledToToday && this.modules.length) { this.scrolledToToday = true; this.scrollToToday() }
      }
    },
    initializePlanningDrafts () {
      // Keep unsaved drafts unless the demand's saved planning changed underneath them.
      const drafts = {}
      const bases = {}
      this.demands.forEach(item => {
        const saved = { storyId: item.storyId || '', plannedStartDate: item.plannedStartDate || '', plannedEndDate: item.plannedEndDate || '' }
        const base = JSON.stringify(saved)
        const current = this.planningDrafts[item.id]
        drafts[item.id] = current && this.planningDraftBases[item.id] === base ? current : saved
        bases[item.id] = base
      })
      this.planningDraftBases = bases
      this.planningDrafts = drafts
    },
    storiesForModule (moduleId) { return this.stories.filter(item => item.moduleId === moduleId) },
    demandsForStory (storyId) { return this.demands.filter(item => item.storyId === storyId).sort((a, b) => String(a.plannedStartDate || '9999').localeCompare(String(b.plannedStartDate || '9999')) || Number(a.sortOrder || 0) - Number(b.sortOrder || 0)) },
    demandVisible (demand) {
      if (this.hideCompleted && this.isCompleted(demand)) return false
      if (this.onlyLate && !this.isLate(demand)) return false
      return true
    },
    visibleDemandsForStory (storyId) {
      const story = this.stories.find(item => item.id === storyId)
      const module = story && this.modules.find(item => item.id === story.moduleId)
      const groupMatches = !this.normalizedSearch || this.matches(story && story.title) || this.matches(module && module.name)
      return this.demandsForStory(storyId).filter(demand => this.demandVisible(demand) && (groupMatches || this.matches(demand.title, demand.displayId, demand.owner)))
    },
    visibleStoriesForModule (moduleId) {
      const stories = this.storiesForModule(moduleId)
      if (!this.filtersActive) return stories
      return stories.filter(story => this.visibleDemandsForStory(story.id).length || (!this.onlyLate && !this.hideCompleted && this.normalizedSearch && this.matches(story.title)))
    },
    matches (...values) {
      if (!this.normalizedSearch) return true
      return values.some(value => this.normalize(value).includes(this.normalizedSearch))
    },
    normalize (value) { return String(value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim() },
    moduleDemands (moduleId) { const ids = new Set(this.storiesForModule(moduleId).map(item => item.id)); return this.demands.filter(item => ids.has(item.storyId)) },
    moduleSummary (moduleId) { return this.summary(this.moduleDemands(moduleId)) },
    storySummary (storyId) { return this.summary(this.demandsForStory(storyId)) },
    summary (items) {
      const dated = items.filter(item => item.plannedStartDate && item.plannedEndDate)
      const starts = dated.map(item => item.plannedStartDate).sort(); const ends = dated.map(item => item.plannedEndDate).sort()
      const hours = items.reduce((sum, item) => sum + Number(item.totalHours || 0), 0)
      const weighted = hours > 0 ? items.reduce((sum, item) => sum + Number(item.progress || 0) * Number(item.totalHours || 0), 0) / hours : (items.length ? items.reduce((sum, item) => sum + Number(item.progress || 0), 0) / items.length : 0)
      return { total: items.length, done: items.filter(this.isCompleted).length, start: starts[0] || '', end: ends[ends.length - 1] || '', progress: Math.round(weighted) }
    },
    async createModule () {
      if (!this.moduleDraft.trim()) return
      await this.run(async () => { await api.createScheduleModule(this.project.id, { name: this.moduleDraft }); this.closeModuleComposer(); await this.loadSchedule() })
    },
    closeModuleComposer () { this.moduleComposerOpen = false; this.moduleDraft = '' },
    openStoryComposer (moduleId) { this.storyComposerModuleId = moduleId; this.storyDraft = ''; this.$set(this.collapsedModules, moduleId, false) },
    closeStoryComposer () { this.storyComposerModuleId = ''; this.storyDraft = '' },
    async createStory (module) {
      if (!this.storyDraft.trim()) return
      // Keep the composer open so several stories can be typed in a row.
      await this.run(async () => { await api.createScheduleStory(module.id, { title: this.storyDraft }); this.storyDraft = ''; await this.loadSchedule() })
    },
    openTaskComposer (storyId) {
      const story = this.stories.find(item => item.id === storyId)
      if (story) this.$set(this.collapsedModules, story.moduleId, false)
      this.$set(this.collapsedStories, storyId, false)
      this.taskComposerStoryId = storyId
      this.taskDraft = { title: '', plannedStartDate: this.today, plannedEndDate: this.today }
    },
    closeTaskComposer () { this.taskComposerStoryId = ''; this.taskDraft = { title: '', plannedStartDate: '', plannedEndDate: '' } },
    closeComposers () { this.closeTaskComposer(); this.closeStoryComposer(); this.closeModuleComposer() },
    onKeydown (event) {
      // Dialogs opened from the schedule handle their own Escape.
      if (event.key !== 'Escape' || document.querySelector('.v-dialog--active')) return
      this.handleEscape()
    },
    handleEscape () {
      if (this.taskComposerStoryId || this.storyComposerModuleId || this.moduleComposerOpen) this.closeComposers()
      else if (this.unscheduledOpen) this.unscheduledOpen = false
      else if (this.fullscreen) this.fullscreen = false
    },
    toggleFullscreen () { this.fullscreen = !this.fullscreen },
    exitToBoard () { this.fullscreen = false; this.$emit('back') },
    startResize (event) {
      this.resizing = true
      this.resizeStart = { x: event.clientX, width: this.leftWidth }
      window.addEventListener('mousemove', this.onResize)
      window.addEventListener('mouseup', this.stopResize)
    },
    onResize (event) {
      const scroller = this.$refs.sheetScroll
      const max = Math.max(320, Math.min(1100, (scroller ? scroller.clientWidth : window.innerWidth) - 200))
      this.leftWidth = Math.round(Math.min(max, Math.max(260, this.resizeStart.width + event.clientX - this.resizeStart.x)))
    },
    stopResize () {
      window.removeEventListener('mousemove', this.onResize)
      window.removeEventListener('mouseup', this.stopResize)
      if (!this.resizing) return
      this.resizing = false
      this.columnsSizedByUser = true
      writeStored('demandesk.schedule.leftWidth', this.leftWidth)
    },
    toggleColumns () {
      this.leftWidth = this.compactColumns ? 860 : 320
      this.columnsSizedByUser = true
      writeStored('demandesk.schedule.leftWidth', this.leftWidth)
    },
    async createTask (story) {
      if (!this.taskDraft.title.trim()) return
      await this.run(async () => {
        const created = await api.createDemand({ projectId: this.project.id, company: this.project.name, contractType: this.project.contractType || 'Demandesk', title: this.taskDraft.title.trim(), priority: 2, status: 'Backlog', storyId: story.id, plannedStartDate: this.taskDraft.plannedStartDate, plannedEndDate: this.taskDraft.plannedEndDate, progress: 0, sortOrder: 0 })
        this.$emit('demand-created', created)
        // Keep the composer open with the same dates so several tasks can be typed in a row.
        this.taskDraft = { ...this.taskDraft, title: '' }
      })
    },
    async planDemand (demand) { await this.updateDemand(demand, { ...this.planningDrafts[demand.id] }) },
    async updateDemandField (demand, field, event) {
      const input = event.target
      const numeric = field === 'progress' || field === 'priority'
      const value = numeric ? Number(input.value) : input.value
      if (field === 'title' && value.trim() === demand.title) return
      const saved = await this.updateDemand(demand, { [field]: value })
      // Controls are bound one-way, so a rejected edit must be put back by hand.
      if (!saved) input.value = numeric ? Number(demand[field] || 0) : (demand[field] || '')
    },
    moveCell (event, direction) {
      const cells = Array.from(this.$el.querySelectorAll(`[data-cell="${event.target.dataset.cell}"]`))
      const next = cells[cells.indexOf(event.target) + direction]
      // Moving focus blurs the current cell, which saves it.
      if (next) { next.focus(); next.select() } else event.target.blur()
    },
    revertCell (event, value) {
      event.target.value = value
      event.target.blur()
    },
    statusOptionsFor (demand) {
      const options = this.statusOptions.length ? this.statusOptions : ['Backlog', 'Pendente', 'Em andamento', 'Concluida']
      return options.includes(demand.status) ? options : [demand.status, ...options]
    },
    ownerOptionsFor (demand) {
      if (!demand.ownerUserId || this.ownerOptions.some(option => option.value === demand.ownerUserId)) return this.ownerOptions
      return [{ value: demand.ownerUserId, text: demand.owner || 'Responsável atual' }, ...this.ownerOptions]
    },
    priorityLabel (priority) { return (this.priorityOptions.find(option => option.value === Number(priority)) || {}).text || '—' },
    updateDemand (demand, patch) {
      return this.run(async () => { const updated = await api.updateDemand(demand.id, patch); this.$emit('demand-updated', updated) })
    },
    beginModuleEditing (module) { this.editingModuleId = module.id; this.editingStoryId = ''; this.editingLabel = module.name },
    beginStoryEditing (story) { this.editingStoryId = story.id; this.editingModuleId = ''; this.editingLabel = story.title },
    cancelEditing () { this.editingModuleId = ''; this.editingStoryId = ''; this.editingLabel = '' },
    async saveModule (module) {
      if (this.editingModuleId !== module.id) return
      const name = this.editingLabel.trim()
      if (!name || name === module.name) { this.cancelEditing(); return }
      await this.run(async () => { await api.updateScheduleModule(module.id, { name }); this.cancelEditing(); await this.loadSchedule() })
    },
    async saveStory (story) {
      if (this.editingStoryId !== story.id) return
      const title = this.editingLabel.trim()
      if (!title || title === story.title) { this.cancelEditing(); return }
      await this.run(async () => { await api.updateScheduleStory(story.id, { title }); this.cancelEditing(); await this.loadSchedule() })
    },
    async removeModule (module) { if (!window.confirm(`Excluir o módulo “${module.name}”? As demandas ficarão sem história.`)) return; await this.run(async () => { await api.deleteScheduleModule(module.id); await this.loadSchedule(); this.$emit('refresh-demands') }) },
    async removeStory (story) { if (!window.confirm(`Excluir a história “${story.title}”? As demandas ficarão sem história.`)) return; await this.run(async () => { await api.deleteScheduleStory(story.id); await this.loadSchedule(); this.$emit('refresh-demands') }) },
    async run (action) { this.saving = true; this.error = ''; try { await action(); return true } catch (error) { this.error = this.errorMessage(error); return false } finally { this.saving = false } },
    errorMessage (error) { return error?.response?.data?.message || error?.message || 'Não foi possível atualizar o cronograma.' },
    toggleModule (id) { this.$set(this.collapsedModules, id, !this.collapsedModules[id]) },
    toggleStory (id) { this.$set(this.collapsedStories, id, !this.collapsedStories[id]) },
    setAllCollapsed (collapsed) {
      const next = {}
      this.modules.forEach(module => { next[module.id] = collapsed })
      this.collapsedModules = next
      if (!collapsed) this.collapsedStories = {}
    },
    toggleUnscheduled () { this.unscheduledOpen = !this.unscheduledOpen },
    isModuleCollapsed (id) { return !this.filtersActive && Boolean(this.collapsedModules[id]) },
    isStoryCollapsed (id) { return !this.filtersActive && Boolean(this.collapsedStories[id]) },
    isCompleted (demand) { return String(demand.status || '').toLowerCase().includes('conclu') },
    isLate (demand) { return Boolean(demand.plannedEndDate) && demand.plannedEndDate < this.today && !this.isCompleted(demand) },
    isPastDue (demand) { return Boolean(demand.dueDate) && ((demand.plannedEndDate && demand.plannedEndDate > demand.dueDate) || (demand.dueDate < this.today && !this.isCompleted(demand))) },
    statusClass (status) {
      const value = this.normalize(status)
      if (value.includes('conclu')) return 'status-dot done'
      if (value.includes('andamento') || value.includes('desenvolvimento')) return 'status-dot progress'
      if (value.includes('pendente')) return 'status-dot pending'
      return 'status-dot'
    },
    barTooltip (demand) {
      const parts = [`${demand.displayId} · ${demand.title}`, `${this.formatShortDate(demand.plannedStartDate)} → ${this.formatShortDate(demand.plannedEndDate)} · ${Number(demand.progress || 0)}%`, demand.status]
      if (demand.owner) parts.push(`Responsável: ${demand.owner}`)
      if (demand.dueDate) parts.push(`Prazo: ${this.formatShortDate(demand.dueDate)}`)
      if (this.isLate(demand)) parts.push('Atrasada')
      return parts.join('\n')
    },
    dueOffset (demand) {
      if (!demand.dueDate) return -1
      const offset = this.daysBetween(this.timelineBounds.start, demand.dueDate)
      return offset >= 0 && offset < this.timelineDays.length ? offset * this.dayWidth + this.dayWidth / 2 : -1
    },
    dateOnly (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    addDays (date, amount) { const value = new Date(`${date}T12:00:00`); value.setDate(value.getDate() + amount); return this.dateOnly(value) },
    daysBetween (start, end) { return Math.round((new Date(`${end}T12:00:00`) - new Date(`${start}T12:00:00`)) / DAY) },
    barStyle (item) {
      const start = item.start || item.plannedStartDate; const end = item.end || item.plannedEndDate
      if (!start || !end) return {}
      const left = Math.max(0, this.daysBetween(this.timelineBounds.start, start)) * this.dayWidth
      const width = Math.max(this.dayWidth, (this.daysBetween(start, end) + 1) * this.dayWidth)
      return { left: `${left}px`, width: `${Math.min(width, this.timelineWidth - left)}px` }
    },
    formatShortDate (date) { return date ? new Date(`${date}T12:00:00`).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '—' },
    setZoom (mode) {
      const scroller = this.$refs.sheetScroll
      // Keep the date at the left edge of the timeline in view when changing scale.
      const anchorDays = scroller ? scroller.scrollLeft / this.dayWidth : 0
      this.zoom = mode
      this.$nextTick(() => { if (this.$refs.sheetScroll) this.$refs.sheetScroll.scrollLeft = anchorDays * this.dayWidth })
    },
    scrollToToday () {
      this.$nextTick(() => {
        const scroller = this.$refs.sheetScroll
        if (!scroller || this.todayOffset < 0) return
        const visibleTimeline = Math.max(0, scroller.clientWidth - this.leftWidth)
        scroller.scrollLeft = Math.max(0, this.todayOffset - visibleTimeline / 4)
      })
    },
    updateSheetHeight () {
      this.$nextTick(() => {
        const scroller = this.$refs.sheetScroll
        if (!scroller) return
        // Until the user drags the divider, size the table to the space available.
        if (!this.columnsSizedByUser) this.leftWidth = scroller.clientWidth >= 1300 ? 860 : scroller.clientWidth >= 1000 ? 600 : 320
        const top = scroller.getBoundingClientRect().top + window.scrollY
        this.sheetMaxHeight = Math.max(320, window.innerHeight - Math.max(0, top - window.scrollY) - (this.fullscreen ? 0 : 16))
      })
    }
  }
}
</script>

<style scoped>
.schedule-workspace { position: relative; min-width: 0; color: #f4f4f5; background: #101011; }
.schedule-workspace.fullscreen { height: 100vh; }
.schedule-toolbar { min-height: 48px; display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 6px 12px; border-bottom: 1px solid #29292c; background: #131314; }
.schedule-heading { flex: 0 0 auto; margin-right: 8px; line-height: 1.2; }
.schedule-toolbar h2 { display: flex; align-items: baseline; gap: 7px; max-width: 320px; margin: 0; overflow: hidden; font-size: 15px; letter-spacing: 0; white-space: nowrap; text-overflow: ellipsis; }
.schedule-toolbar h2 small { color: #8b8b91; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.schedule-toolbar p { margin: 1px 0 0; color: #929299; font-size: 11px; white-space: nowrap; }
.stat-link { padding: 0; border: 0; background: transparent; color: inherit; font: inherit; text-decoration: underline dotted; text-underline-offset: 3px; cursor: pointer; }
.stat-link:hover, .stat-link.active { color: #f4f4f5; }
.stat-link.late { color: #f87171; }
.stat-link.late.active { font-weight: 700; }
.schedule-zoom { display: flex; align-items: center; gap: 2px; padding: 2px; border: 1px solid #303033; border-radius: 6px; background: #19191b; }
.schedule-zoom button { height: 24px; padding: 0 9px; border: 0; border-radius: 4px; background: transparent; color: #b9b9bf; font-size: 11px; font-weight: 600; cursor: pointer; }
.schedule-zoom button.active { background: #f4f4f5; color: #111; }
.schedule-button { height: 30px; padding: 0 11px; border: 1px solid #343438; border-radius: 6px; background: #171719; color: #ededee; font-size: 12px; font-weight: 600; white-space: nowrap; cursor: pointer; }
.schedule-button:hover { border-color: #52525b; }
.schedule-button.is-primary { border-color: #f4f4f5; background: #f4f4f5; color: #111; }
.schedule-workspace .schedule-search { flex: 1 1 140px; width: auto; max-width: 260px; height: 30px; }
.view-toggle { height: 30px; padding: 0 9px; border: 1px solid #303034; border-radius: 6px; background: #19191b; color: #b9b9bf; font-size: 11px; font-weight: 600; white-space: nowrap; cursor: pointer; }
.view-toggle:hover { color: #f4f4f5; border-color: #4a4a50; }
.view-toggle.active { border-color: #d4d4d8; background: #27272a; color: #f4f4f5; }
.icon-button { width: 30px; height: 30px; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; border: 1px solid #303034; border-radius: 6px; background: #19191b; color: #b9b9bf; font-size: 13px; font-weight: 700; cursor: pointer; }
.icon-button:hover, .icon-button.active { border-color: #d4d4d8; color: #f4f4f5; }
.toolbar-divider { width: 1px; height: 20px; margin: 0 2px; background: #303034; }
.toolbar-spacer { flex: 1; }
.legend-wrap { position: relative; }
.schedule-legend { position: absolute; top: calc(100% + 6px); right: 0; z-index: 30; display: none; flex-direction: column; gap: 7px; min-width: 170px; padding: 10px 12px; border: 1px solid #34343a; border-radius: 8px; background: #1c1c1f; box-shadow: 0 10px 30px rgba(0,0,0,.5); color: #c4c4ca; font-size: 11px; }
.legend-wrap:hover .schedule-legend, .legend-wrap:focus-within .schedule-legend { display: flex; }
.schedule-legend span { display: inline-flex; align-items: center; gap: 8px; }
.legend-swatch { width: 14px; height: 8px; border-radius: 2px; }
.legend-swatch.priority-1 { background: #ef4444; }.legend-swatch.priority-2 { background: #f59e0b; }.legend-swatch.priority-3 { background: #22c55e; }
.legend-swatch.done { background: #52525b; }
.legend-swatch.late { background: transparent; box-shadow: inset 0 0 0 2px #f87171; }
.legend-due { width: 7px; height: 7px; margin: 0 3px; background: #e4e4e7; transform: rotate(45deg); }
.legend-today { width: 2px; height: 12px; margin: 0 6px; background: #ef4444; }
.schedule-alert { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin: 8px 12px; padding: 8px 12px; border: 1px solid #7f1d1d; border-radius: 6px; background: #261414; color: #fca5a5; font-size: 12px; }
.schedule-alert button { border: 0; background: transparent; color: inherit; font-size: 16px; cursor: pointer; }
.schedule-composer { display: flex; gap: 8px; padding: 8px 12px; border-bottom: 1px solid #292929; background: #151515; }
.schedule-composer input { flex: 1; }
.schedule-workspace input, .schedule-workspace select { height: 32px; min-width: 0; border: 1px solid #333337; border-radius: 5px; background: #202023; color: #ededee; padding: 0 8px; font-size: 12px; outline: none; color-scheme: dark; }
.schedule-workspace input:focus, .schedule-workspace select:focus { border-color: #a1a1aa; box-shadow: 0 0 0 1px #a1a1aa; }
.schedule-workspace input:disabled, .schedule-workspace select:disabled { opacity: .6; }
.schedule-composer button, .story-composer button, .task-composer-row button, .unscheduled-item > button:last-child { height: 30px; border: 1px solid #3b3b40; border-radius: 5px; background: #29292d; color: #f4f4f5; padding: 0 12px; font-size: 12px; cursor: pointer; }
.story-composer button.ghost, .task-composer-row button.ghost { padding: 0 8px; background: transparent; }

.schedule-body { position: relative; }
.unscheduled-drawer { position: absolute; top: 0; right: 0; z-index: 20; width: min(440px, 100%); display: flex; flex-direction: column; border-left: 1px solid #34343a; background: #161618; box-shadow: -16px 0 40px rgba(0,0,0,.45); }
.unscheduled-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; padding: 10px 12px 6px; }
.unscheduled-heading strong { display: flex; align-items: center; gap: 6px; font-size: 13px; }
.unscheduled-heading b { padding: 0 7px; border: 1px solid #35353a; border-radius: 999px; font-size: 11px; }
.unscheduled-heading small { display: block; margin-top: 2px; color: #8f8f96; font-size: 11px; }
.unscheduled-tools { display: flex; align-items: center; gap: 6px; padding: 0 12px 8px; color: #8f8f96; font-size: 11px; cursor: pointer; }
.schedule-workspace .unscheduled-tools input { height: auto; }
.unscheduled-list { flex: 1; overflow: auto; border-top: 1px solid #29292d; }
.unscheduled-item { display: grid; grid-template-columns: 1fr 1fr auto; gap: 6px; padding: 8px 12px; border-bottom: 1px solid #252528; }
.unscheduled-item .unscheduled-title, .unscheduled-item select { grid-column: 1 / -1; }
.unscheduled-title { min-width: 0; display: flex; flex-direction: column; align-items: flex-start; padding: 0; border: 0; background: transparent; color: #ededee; font-size: 13px; text-align: left; cursor: pointer; }
.unscheduled-title span { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.unscheduled-title:hover span { text-decoration: underline; }
.unscheduled-title small { color: #818188; font-size: 10px; }
.unscheduled-empty { padding: 12px; color: #8f8f96; font-size: 12px; }
.column-resizer { position: absolute; top: 0; z-index: 12; width: 6px; cursor: col-resize; }
.column-resizer:hover, .column-resizer.active { background: rgba(212,212,216,.35); }

.schedule-empty { min-height: 340px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #8f8f96; }
.schedule-empty strong { color: #ededee; font-size: 16px; }
.schedule-empty button { margin-top: 8px; padding: 9px 14px; border: 1px solid #3a3a3e; border-radius: 6px; background: #ededee; color: #111; cursor: pointer; }

.schedule-sheet-scroll { overflow: auto; scrollbar-width: thin; scrollbar-color: #3f3f46 #111; }
.schedule-sheet-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.schedule-sheet-scroll::-webkit-scrollbar-track { background: #111; }
.schedule-sheet-scroll::-webkit-scrollbar-thumb { border: 2px solid #111; border-radius: 6px; background: #3f3f46; }
.schedule-sheet-scroll::-webkit-scrollbar-thumb:hover { background: #52525b; }
.schedule-sheet { --date-col: 90px; --pct-col: 40px; --task-columns: minmax(180px, 1fr) 100px var(--date-col) var(--date-col) var(--pct-col); }
.schedule-sheet.wide { --task-columns: minmax(200px, 1fr) 100px 108px 58px var(--date-col) var(--date-col) var(--pct-col); }
.schedule-sheet.compact { --task-columns: minmax(0, 1fr) var(--pct-col); }
.schedule-sheet { display: grid; grid-template-columns: var(--left-width) var(--timeline-width); width: max-content; min-width: 100%; background: #101011; }
.schedule-left-cell { position: sticky; left: 0; z-index: 4; width: var(--left-width); border-right: 1px solid #343438; border-bottom: 1px solid #252528; background: #171719; }
.schedule-right-cell { position: relative; border-bottom: 1px solid #252528; background-color: #111; }

.schedule-grid-header { top: 0; z-index: 9; height: 42px; display: grid; grid-template-columns: var(--task-columns); align-items: end; gap: 6px; padding: 0 10px 8px; background: #151517; color: #85858c; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.schedule-grid-header .align-center { text-align: center; }
.schedule-timeline-header { position: sticky; top: 0; z-index: 7; height: 42px; width: var(--timeline-width); overflow: hidden; background: #151517; }
.timeline-months { display: flex; height: 20px; border-bottom: 1px solid #29292c; }
.timeline-months span { flex: 0 0 auto; overflow: hidden; padding: 3px 6px 0; border-right: 1px solid #34343a; color: #b4b4bb; font-size: 10px; font-weight: 700; white-space: nowrap; }
.timeline-days { display: flex; height: 22px; }
.timeline-day { flex: 0 0 auto; position: relative; height: 100%; color: #7f7f86; font-size: 9px; }
.timeline-day.weekend { background: #131315; }
.timeline-day.marked { box-shadow: inset 1px 0 0 #2e2e33; }
.timeline-day.today { background: #3a1616; color: #fecaca; }
.timeline-day b { position: absolute; bottom: 5px; left: 3px; white-space: nowrap; font-weight: 600; }

.group-row { height: 32px; display: grid; grid-template-columns: 22px minmax(0, 1fr) 46px var(--date-col) var(--date-col) var(--pct-col); align-items: center; gap: 6px; padding: 0 10px; }
.compact .group-row { grid-template-columns: 22px minmax(0, 1fr) var(--pct-col); }
.module-row { background: #1f1f22; }
.story-row { background: #1a1a1c; padding-left: 24px; }
.collapse-button { width: 22px; height: 22px; border: 0; border-radius: 4px; background: transparent; color: #a1a1aa; font-size: 12px; cursor: pointer; }
.collapse-button:hover { background: #2a2a2e; color: #f4f4f5; }
.group-title { min-width: 0; display: flex; flex-direction: column; line-height: 1.2; }
.group-title small { color: #77777f; font-size: 9px; text-transform: uppercase; }
.group-title strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12.5px; font-weight: 600; }
.module-row .group-title strong { font-size: 13px; font-weight: 700; }
.group-title input { width: 100%; height: 28px; }
.group-metric, .group-date, .group-progress { color: #98989f; font-size: 11px; text-align: center; }
.group-progress { color: #d4d4d8; font-weight: 700; }
.row-actions { position: absolute; top: 50%; right: 8px; display: flex; gap: 2px; padding: 2px; border: 1px solid #34343a; border-radius: 6px; background: #232326; box-shadow: 0 4px 14px rgba(0,0,0,.45); opacity: 0; pointer-events: none; transform: translateY(-50%); transition: opacity 120ms ease; }
.group-row:hover .row-actions, .group-row:focus-within .row-actions { opacity: 1; pointer-events: auto; }
.row-actions button { height: 22px; padding: 0 8px; border: 0; border-radius: 4px; background: transparent; color: #d4d4d8; font-size: 11px; white-space: nowrap; cursor: pointer; }
.row-actions button:hover { background: #333338; color: #fff; }
.row-actions button.danger { color: #fca5a5; }

.timeline-cell { height: 32px; }
.group-timeline { background-color: #19191b; }
.story-timeline { background-color: #151517; }
.today-line { position: absolute; z-index: 3; top: 0; bottom: 0; width: 2px; margin-left: -1px; background: #ef4444; opacity: .75; pointer-events: none; }
.today-line.header { z-index: 8; }
.summary-bar { position: absolute; z-index: 1; top: 50%; height: 8px; border-radius: 2px; overflow: hidden; background: #3f3f46; transform: translateY(-50%); }
.summary-bar > i { position: absolute; inset: 0 auto 0 0; background: #d4d4d8; }
.summary-bar.story-summary { height: 6px; }
.summary-bar.story-summary > i { background: #a1a1aa; }

.task-row { height: 30px; display: grid; grid-template-columns: var(--task-columns); align-items: center; gap: 6px; padding: 0 10px 0 46px; }

.task-row:hover, .task-row:hover + .task-timeline { background-color: #1c1c1f; }
.task-title-cell { position: relative; min-width: 0; display: flex; align-items: center; gap: 4px; }
.task-id { flex: 0 0 auto; padding: 0 2px; border: 0; background: transparent; color: #74747b; font-size: 9px; cursor: pointer; }
.task-id:hover { color: #f4f4f5; text-decoration: underline; }
.open-task { position: absolute; top: 50%; right: 2px; width: 22px; height: 22px; display: inline-flex; align-items: center; justify-content: center; border: 1px solid #3a3a3f; border-radius: 4px; background: #232326; color: #d4d4d8; opacity: 0; pointer-events: none; transform: translateY(-50%); cursor: pointer; }
.task-row:hover .open-task { opacity: 1; pointer-events: auto; }
.open-task:hover { border-color: #a1a1aa; color: #fff; }
.schedule-workspace .task-row .title-input { flex: 1; height: 24px; padding: 0 28px 0 4px; font-size: 12px; font-weight: 500; color: #e4e4e7; text-overflow: ellipsis; }
.task-row.is-done .title-input, .task-row.is-done .task-title { color: #8b8b91; }
.status-cell { min-width: 0; display: flex; align-items: center; gap: 4px; }
.status-cell > span { overflow: hidden; color: #a1a1aa; font-size: 10px; text-overflow: ellipsis; white-space: nowrap; }
.cell-select { width: 100%; min-width: 0; height: 24px; padding: 0 4px; border: 1px solid transparent; border-radius: 5px; background: transparent; color: #c4c4ca; font-size: 11px; text-overflow: ellipsis; appearance: none; -webkit-appearance: none; outline: none; cursor: pointer; }
.schedule-workspace .task-row .cell-select { height: 24px; border-color: transparent; background: transparent; }
.schedule-workspace .task-row .cell-select:hover { border-color: #3a3a3f; background: #202023; }
.schedule-workspace .task-row .cell-select:focus { border-color: #a1a1aa; background: #202023; box-shadow: none; }
.cell-select option { background: #1c1c1f; color: #ededee; }
.cell-select.muted, .cell-text { color: #6f6f76; }
.cell-text { overflow: hidden; padding: 0 4px; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.priority-select { font-weight: 700; }
.priority-select.priority-1 { color: #f87171; }.priority-select.priority-2 { color: #fbbf24; }.priority-select.priority-3 { color: #4ade80; }
.task-title { flex: 1; min-width: 0; overflow: hidden; padding: 0 4px; border: 0; background: transparent; color: #e4e4e7; font-size: 12px; font-weight: 500; text-align: left; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; }
.task-title:hover { text-decoration: underline; }
.status-dot { flex: 0 0 auto; width: 7px; height: 7px; border-radius: 50%; background: #71717a; }
.status-dot.pending { background: #94a3b8; }.status-dot.progress { background: #f4b000; }.status-dot.done { background: #22c55e; }
.schedule-workspace .task-row input { width: 100%; height: 24px; padding: 0 4px; border-color: transparent; background: transparent; font-size: 11px; }
.schedule-workspace .task-row input:hover:not(:disabled) { border-color: #3a3a3f; background: #202023; }
.schedule-workspace .task-row input:focus { border-color: #a1a1aa; background: #202023; }
.schedule-workspace .task-row input.late { color: #f87171; font-weight: 600; }
.schedule-workspace .task-row input[type=number] { padding: 0; text-align: center; -moz-appearance: textfield; }
.task-row input[type=number]::-webkit-inner-spin-button, .task-row input[type=number]::-webkit-outer-spin-button { margin: 0; -webkit-appearance: none; }
.task-row input[type=date]::-webkit-calendar-picker-indicator { width: 10px; margin: 0; padding: 0; opacity: .45; cursor: pointer; }
.task-row input[type=date]:hover::-webkit-calendar-picker-indicator { opacity: .9; }
.task-timeline { height: 30px; }
.missing-date { position: absolute; top: 8px; left: 12px; color: #5f5f65; font-size: 10px; }

.timeline-bar { --bar-color: #a1a1aa; position: absolute; z-index: 2; top: 7px; height: 16px; min-width: 4px; border: 1px solid var(--bar-color); border-radius: 4px; background: #111; cursor: pointer; }
.timeline-bar::before { content: ''; position: absolute; inset: 0; border-radius: 3px; background: var(--bar-color); opacity: .3; pointer-events: none; }
.timeline-bar:hover { filter: brightness(1.12); box-shadow: 0 0 0 1px rgba(255,255,255,.5); }
.bar-progress { position: absolute; inset: 0 auto 0 0; border-radius: 3px 0 0 3px; background: var(--bar-color); pointer-events: none; }
.bar-label { position: absolute; top: 50%; left: calc(100% + 6px); color: #d4d4d8; font-size: 11px; white-space: nowrap; pointer-events: none; transform: translateY(-50%); }
.bar-label em { color: #8b8b91; font-style: normal; }
.timeline-bar.priority-1 { --bar-color: #ef4444; }.timeline-bar.priority-2 { --bar-color: #f59e0b; }.timeline-bar.priority-3 { --bar-color: #22c55e; }
.timeline-bar.completed { --bar-color: #52525b; }
.timeline-bar.completed .bar-label { color: #8b8b91; }
.timeline-bar.late { box-shadow: 0 0 0 2px #111, 0 0 0 3px #f87171; }
.timeline-bar.late .bar-label { color: #fca5a5; }
.due-marker { position: absolute; z-index: 3; top: 50%; width: 9px; height: 9px; margin-left: -4px; border: 1px solid #111; background: #e4e4e7; transform: translateY(-50%) rotate(45deg); }
.due-marker.overdue { background: #f87171; }

.add-row { height: 28px; display: flex; align-items: center; padding-left: 46px; }
.add-row.story-level { padding-left: 34px; }
.add-row button { border: 0; background: transparent; color: #8b8b92; font-size: 11px; cursor: pointer; }
.add-row button:hover { color: #f4f4f5; }
.add-row-timeline { height: 28px; background: #101011; }
.story-composer, .task-composer-row { height: 36px; display: grid; align-items: center; gap: 6px; padding: 4px 10px 4px 46px; background: #1a1a1d; }
.story-composer { grid-template-columns: 1fr auto auto; padding-left: 34px; }
.task-composer-row { grid-template-columns: minmax(160px, 1fr) 118px 118px auto auto; }
.compact .task-composer-row { grid-template-columns: minmax(0, 1fr) auto auto; }
.compact .task-composer-row input[type=date] { display: none; }
.schedule-workspace .story-composer input, .schedule-workspace .task-composer-row input { height: 28px; }
.composer-timeline { height: 36px; background: #141416; }
.schedule-no-results { display: flex; align-items: center; height: 60px; padding: 0 14px; color: #8f8f96; font-size: 12px; }

@media (max-width: 1050px) {
  .toolbar-spacer { flex-basis: 100%; }
  .schedule-workspace .schedule-search { max-width: none; }
}
</style>
